import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Shield, RefreshCw, Search, Calendar, ChevronLeft, ChevronRight,
  User, FileText, Edit, Trash2, Plus, Eye, Download, Upload, Settings, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuditLog = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const itemsPerPage = 25;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = {
        page: currentPage,
        per_page: itemsPerPage
      };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get(`${API_URL}/audit-logs`, { headers, params });
      const data = response.data;
      if (Array.isArray(data)) {
        setLogs(data);
        setTotalCount(data.length);
      } else {
        setLogs(data.items || data.logs || []);
        setTotalCount(data.total || data.count || (data.items || data.logs || []).length);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement du journal d\'audit');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, currentPage, dateFrom, dateTo, searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const lower = (action || '').toLowerCase();
    if (lower.includes('creat') || lower.includes('ajout')) return <Plus className="w-4 h-4 text-green-600" />;
    if (lower.includes('modif') || lower.includes('updat') || lower.includes('edit')) return <Edit className="w-4 h-4 text-blue-600" />;
    if (lower.includes('supprim') || lower.includes('delet')) return <Trash2 className="w-4 h-4 text-red-600" />;
    if (lower.includes('consult') || lower.includes('view') || lower.includes('read')) return <Eye className="w-4 h-4 text-slate-600" />;
    if (lower.includes('export') || lower.includes('download')) return <Download className="w-4 h-4 text-purple-600" />;
    if (lower.includes('import') || lower.includes('upload')) return <Upload className="w-4 h-4 text-orange-600" />;
    if (lower.includes('config') || lower.includes('param')) return <Settings className="w-4 h-4 text-slate-600" />;
    if (lower.includes('login') || lower.includes('auth') || lower.includes('connexion')) return <Lock className="w-4 h-4 text-indigo-600" />;
    return <FileText className="w-4 h-4 text-slate-500" />;
  };

  const getActionBadge = (action) => {
    const lower = (action || '').toLowerCase();
    if (lower.includes('creat') || lower.includes('ajout')) return <Badge className="bg-green-100 text-green-800">{action}</Badge>;
    if (lower.includes('modif') || lower.includes('updat') || lower.includes('edit')) return <Badge className="bg-blue-100 text-blue-800">{action}</Badge>;
    if (lower.includes('supprim') || lower.includes('delet')) return <Badge className="bg-red-100 text-red-800">{action}</Badge>;
    if (lower.includes('login') || lower.includes('connexion')) return <Badge className="bg-indigo-100 text-indigo-800">{action}</Badge>;
    return <Badge variant="outline">{action}</Badge>;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement du journal d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            Journal d'audit
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Historique de toutes les actions effectuées dans le système
          </p>
        </div>
        <Button variant="outline" onClick={fetchLogs}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par utilisateur, action..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Du</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Au</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                />
              </div>
              <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
                <Search className="w-4 h-4 mr-2" />
                Filtrer
              </Button>
              <Button variant="outline" onClick={handleReset}>
                Reinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg font-medium">Aucune entree dans le journal</p>
              <p className="text-slate-400 text-sm mt-1">Les actions seront enregistrees ici automatiquement</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Date</TableHead>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="min-w-[300px]">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, idx) => (
                    <TableRow key={log.id || idx}>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(log.date || log.created_at || log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {log.user_name || log.utilisateur || log.user_email || '-'}
                            </p>
                            {log.user_email && log.user_name && (
                              <p className="text-xs text-slate-400">{log.user_email}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          {getActionBadge(log.action)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 max-w-md truncate">
                          {typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || '-')}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-slate-500">
                  {totalCount} entree(s) - Page {currentPage} / {Math.max(totalPages, 1)}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
